/**
 * Create / edit page for a Monitoring CDN. Reached from the list via
 * `monitors/new` and `monitors/edit?id=`.
 *
 * A Managed/Custom toggle reshapes the form: "Managed" probes an origin
 * (FQDN/IPv4) + hostname and uploads the test object to a path on the CDN;
 * "Custom" probes a full reference URL where the test object was uploaded. The
 * CDN platform is fixed once created (read-only in edit mode).
 *
 * Note: the monitor list shape doesn't return origin/hostname/url, so those
 * can't be prefilled on edit — only name/type/path/cacheable are.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, ArrowLeft, Download } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Checkbox,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafPlatforms,
  useWafMonitors,
  useCreateWafMonitor,
  useUpdateWafMonitor,
  wafExtractError,
  type WafMonitorCreate,
  type WafMonitorUpdate,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';

const TYPE_ITEMS = [
  { value: 'managed', label: 'Managed' },
  { value: 'custom', label: 'Custom' },
];
const ORIGIN_ITEMS = [
  { value: 'FQDN', label: 'FQDN' },
  { value: 'IPv4', label: 'IPv4' },
];

/** The downloadable 1×1 test object the user re-uploads to their origin/CDN. */
const TEST_OBJECT_URL = '/assets/img/1pixel.gif';

/** A stacked field: bold label (+ optional inline extra) over the control + hint. */
function Field({
  label,
  labelExtra,
  hint,
  children,
}: {
  label: string;
  labelExtra?: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontWeight: 600 }}>
        {label}
        {labelExtra}
      </span>
      {children}
      {hint ? (
        <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>{hint}</span>
      ) : null}
    </div>
  );
}

/** The "Test object" block — a download link to the 1×1 gif, in both layouts. */
function TestObject() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontWeight: 600 }}>Test object</span>
      <a
        href={TEST_OBJECT_URL}
        download
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          width: 'fit-content',
          fontWeight: 500,
          color: 'var(--vn-accent-text)',
          textDecoration: 'none',
        }}
      >
        1pixel.gif <Download size={15} />
      </a>
      <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
        Download the test object, and upload it to the path you specified below.
      </span>
    </div>
  );
}

export function WafMonitorForm({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const id = params.get('id');
  const isEdit = !!id;
  const back = () => navigate(`/services/waf/${serviceId}/monitors`);

  const platformsQ = useWafPlatforms(serviceId);
  const platforms = platformsQ.data ?? [];

  // On edit, locate the monitor in the (account-wide) list.
  const monitorsQ = useWafMonitors(serviceId, { page: 1, page_size: 200 }, isEdit);
  const monitor = useMemo(
    () => (isEdit ? (monitorsQ.data?.items ?? []).find((m) => String(m.id) === id) : undefined),
    [isEdit, monitorsQ.data, id],
  );

  // ---- form state ----
  const [seeded, setSeeded] = useState(false);
  const [type, setType] = useState('managed');
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState('');
  // Managed
  const [originType, setOriginType] = useState('FQDN');
  const [originSource, setOriginSource] = useState('');
  const [hostname, setHostname] = useState('');
  const [path, setPath] = useState('');
  const [cacheable, setCacheable] = useState(true);
  // Custom
  const [url, setUrl] = useState('');

  // Seed once: immediately on create, or after the monitor loads on edit.
  useEffect(() => {
    if (seeded) return;
    if (!isEdit) { setSeeded(true); return; }
    if (!monitor) return;
    setType(monitor.type === 'custom' ? 'custom' : 'managed');
    setName(monitor.name ?? '');
    setPlatform(monitor.platform_name ?? '');
    setPath(monitor.path ?? '');
    setCacheable(monitor.is_cacheable ?? true);
    setSeeded(true);
  }, [isEdit, monitor, seeded]);

  const create = useCreateWafMonitor(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Monitoring CDN created' }); trackFeatureAction('waf', 'monitor', 'create'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateWafMonitor(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Monitoring CDN updated' }); trackFeatureAction('waf', 'monitor', 'edit'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const pending = create.isPending || update.isPending;

  const isCustom = type === 'custom';
  const valid =
    !!name.trim() &&
    (isEdit || !!platform) &&
    (isCustom ? !!url.trim() : !!originSource.trim());

  const submit = () => {
    if (!valid) return;
    const common = isCustom
      ? { name: name.trim(), type: 'custom', url: url.trim() }
      : {
          name: name.trim(),
          type: 'managed',
          origin: { type: originType, source: originSource.trim() },
          hostname: hostname.trim() || undefined,
          is_cacheable: cacheable,
          path: path.trim() || undefined,
        };
    if (isEdit && monitor) {
      update.mutate({ id: monitor.id, input: common as WafMonitorUpdate });
    } else {
      create.mutate({ platform_name: platform, ...common } as WafMonitorCreate);
    }
  };

  const loadingEdit = isEdit && monitorsQ.isPending && !seeded;
  const notFound = isEdit && !monitorsQ.isPending && !monitor;

  return (
    <Container size="md">
      <Button
        variant="ghost"
        size="sm"
        startIcon={<ArrowLeft size={15} />}
        onClick={back}
        style={{ marginBottom: 'var(--vn-space-2)' }}
      >
        Back to Monitoring CDN
      </Button>
      <PageHeader
        icon={<Activity size={22} />}
        title={isEdit ? 'Edit Monitoring CDN' : 'Add Monitoring CDN'}
        description="Monitor the performance of your origin through a specific CDN. The created monitor will be available on the Dynamic Data Feed for that CDN."
      />

      <Card>
        <CardBody>
          {loadingEdit ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 48 }} />
              ))}
            </div>
          ) : notFound ? (
            <Alert tone="danger" title="Monitor not found" actions={<button onClick={back}>Back</button>}>
              This Monitoring CDN no longer exists or couldn’t be loaded.
            </Alert>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Field label="Type">
                <div style={{ alignSelf: 'flex-start' }}>
                  <SegmentedControl items={TYPE_ITEMS} value={type} onValueChange={setType} aria-label="Monitor type" />
                </div>
              </Field>

              <Field label="Monitoring CDN Name">
                <Input
                  placeholder="Enter Monitoring CDN Name here..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={pending}
                />
              </Field>

              <Field label="CDN" hint={isEdit ? 'Fixed once the monitor is created.' : undefined}>
                <Select value={platform} onValueChange={setPlatform} disabled={pending || isEdit || platformsQ.isPending}>
                  <SelectTrigger aria-label="CDN">
                    <SelectValue placeholder={platformsQ.isPending ? 'Loading…' : 'Choose a CDN...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {platforms.map((p) => (
                      <SelectItem key={p.id} value={p.name}>{p.display_name || p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {isCustom ? (
                <>
                  <TestObject />
                  <Field
                    label="Reference URL / path"
                    hint="Provide the path where your test object was uploaded (file size < 100KB). Please include the Access-Control-Allow-Origin and Timing-Allow-Origin in the response header."
                  >
                    <Input
                      placeholder="https://example.com/test.gif"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      disabled={pending}
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field
                    label="Origin address"
                    labelExtra={
                      <SegmentedControl
                        items={ORIGIN_ITEMS}
                        value={originType}
                        onValueChange={setOriginType}
                        size="sm"
                        aria-label="Origin address type"
                      />
                    }
                    hint="This specifies the location of your origin, either FQDN or IPv4."
                  >
                    <Input
                      placeholder="www.example.com"
                      value={originSource}
                      onChange={(e) => setOriginSource(e.target.value)}
                      disabled={pending}
                    />
                  </Field>

                  <Field label="Hostname" hint="This specifies the domain which the performance will be measured.">
                    <Input
                      placeholder="www.example.com"
                      value={hostname}
                      onChange={(e) => setHostname(e.target.value)}
                      disabled={pending}
                    />
                  </Field>

                  <TestObject />

                  <Field
                    label="Test object path"
                    labelExtra={
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontWeight: 400,
                          fontSize: 'var(--vn-font-size-sm)',
                          cursor: 'pointer',
                        }}
                      >
                        <Checkbox
                          checked={cacheable}
                          onCheckedChange={(v) => setCacheable(v === true)}
                          disabled={pending}
                        />
                        Make the object cacheable on the CDN.
                      </label>
                    }
                    hint="This is the path in the origin where the test object should be uploaded."
                  >
                    <Input
                      placeholder="/images/1pixel.gif"
                      value={path}
                      onChange={(e) => setPath(e.target.value)}
                      disabled={pending}
                    />
                  </Field>
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-2)' }}>
                <Button variant="ghost" onClick={back} disabled={pending}>
                  Cancel
                </Button>
                <Button onClick={submit} disabled={pending || !valid}>
                  Save
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
}

export default WafMonitorForm;
